<script lang="ts" setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { accountService } from "@/app/services/AccountService";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import InfoCard from "@/components/atoms/other/InfoCard.vue";
import FormRadioGroup from "@/components/molecules/form/FormRadioGroup.vue";
import Header from "@/components/organisms/common/Header.vue";
import { authStore } from "@/store/auth";
import { useLoader } from "@/utils/loader";
import { useErrorModal, useModal } from "@/utils/modal";
import { useCustomRouter } from "@/utils/router";

const loader = useLoader();
const errorModal = useErrorModal();
const modal = useModal();
const router = useRouter();
const customRouter = useCustomRouter();

const isReceivedFavoriteOfficeOrder = ref(
  authStore.user?.isReceivedFavoriteOfficeOrder
);
const isReceivedLimitedOrder = ref(authStore.user?.isReceivedLimitedOrder);
const isReceivedIndividualOrder = ref(
  authStore.user?.isReceivedIndividualOrder
);
const isReceivedNearbyOfficeOrder = ref(
  authStore.user?.isReceivedNearbyOfficeOrder
);
const isReceivedCancelRecoveryOrderPushForWorker = ref(
  authStore.user?.isReceivedCancelRecoveryOrderPushForWorker
);

const options = [
  { text: "はい", value: true },
  { text: "いいえ", value: false }
];

const formValues = computed(() => ({
  isReceivedFavoriteOfficeOrder: isReceivedFavoriteOfficeOrder.value,
  isReceivedLimitedOrder: isReceivedLimitedOrder.value,
  isReceivedIndividualOrder: isReceivedIndividualOrder.value,
  isReceivedNearbyOfficeOrder: isReceivedNearbyOfficeOrder.value,
  isReceivedCancelRecoveryOrderPushForWorker:
    isReceivedCancelRecoveryOrderPushForWorker.value
}));

async function onSubmit() {
  loader.show();
  try {
    await accountService.patchUser({
      isReceivedFavoriteOfficeOrder: isReceivedFavoriteOfficeOrder.value,
      isReceivedLimitedOrder: isReceivedLimitedOrder.value,
      isReceivedIndividualOrder: isReceivedIndividualOrder.value,
      isReceivedNearbyOfficeOrder: isReceivedNearbyOfficeOrder.value,
      isReceivedCancelRecoveryOrderPushForWorker:
        isReceivedCancelRecoveryOrderPushForWorker.value
    });
    await modal.msgBoxOk("プッシュ通知受信設定を変更しました", {
      okVariant: "success",
      centered: true
    });
    await customRouter.pushIgnoreError({ name: "Mypage" });
  } catch (err) {
    errorModal.showErrorModal(err as Error);
  } finally {
    loader.hide();
  }
}
</script>

<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="router.back()" />
      </template>
      プッシュ通知受信設定
    </Header>
    <CustomValidationObserver
      tag="div"
      class="my-4 mx-3"
      v-slot="{ handleSubmit }"
      :initialValues="formValues"
    >
      <InfoCard body-class="p-2">
        <p class="text-size-14">
          お仕事の募集が開始された際のプッシュ通知の受信設定を変更できます
        </p>
      </InfoCard>
      <BForm>
        <ValidationProvider
          v-slot="validationState"
          name="指名オファーのお仕事"
          rules="required_allow_false"
          vid="isReceivedIndividualOrder"
          class="mt-3"
        >
          <FormRadioGroup
            label="指名オファー"
            name="指名オファー"
            v-model="isReceivedIndividualOrder"
            :options="options"
            :validation-state="validationState"
          />
        </ValidationProvider>
        <ValidationProvider
          v-slot="validationState"
          name="限定公開のお仕事"
          rules="required_allow_false"
          vid="isReceivedLimitedOrder"
          class="mt-3"
        >
          <FormRadioGroup
            label="限定公開のお仕事"
            name="限定公開のお仕事"
            v-model="isReceivedLimitedOrder"
            :options="options"
            :validation-state="validationState"
          />
        </ValidationProvider>
        <ValidationProvider
          v-slot="validationState"
          name="お気に入り事業所のお仕事"
          rules="required_allow_false"
          vid="isReceivedFavoriteOfficeOrder"
          class="mt-3"
        >
          <FormRadioGroup
            label="お気に入り事業所のお仕事"
            name="お気に入り事業所のお仕事"
            v-model="isReceivedFavoriteOfficeOrder"
            :options="options"
            :validation-state="validationState"
          />
        </ValidationProvider>
        <ValidationProvider
          v-slot="validationState"
          name="近くの事業所のお仕事"
          rules="required_allow_false"
          vid="isReceivedNearbyOfficeOrder"
          class="mt-3"
        >
          <FormRadioGroup
            label="近くの事業所のお仕事"
            name="近くの事業所のお仕事"
            v-model="isReceivedNearbyOfficeOrder"
            :options="options"
            :validation-state="validationState"
          />
        </ValidationProvider>
        <ValidationProvider
          v-slot="validationState"
          name="キャンセル発生時の緊急募集のお仕事"
          rules="required_allow_false"
          vid="isReceivedCancelRecoveryOrderPushForWorker"
          class="mt-3"
        >
          <FormRadioGroup
            label="キャンセル発生時の緊急募集のお仕事"
            name="cancel-recovery-order"
            v-model="isReceivedCancelRecoveryOrderPushForWorker"
            :options="options"
            :validation-state="validationState"
          />
        </ValidationProvider>
        <AppButton block class="mt-4" @click="handleSubmit(onSubmit)">
          更新する
        </AppButton>
      </BForm>
    </CustomValidationObserver>
  </div>
</template>
